import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Award,
  BarChart as BarChartIcon,
  CreditCard,
  Users,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

export default function PerkPointDashboard() {
  // Format large Y-axis values as 10k, 20k etc. to avoid label cropping
  const formatYAxisTick = (value: number) => {
    if (Math.abs(value) >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value.toString();
  };

  const businessData = [
    { month: "Jun-25", value: 3200 },
    { month: "Jul-25", value: 4100 },
    { month: "Aug-25", value: 2850 },
    { month: "Sep-25", value: 1950 },
    { month: "Oct-25", value: 2680 },
    { month: "Nov-25", value: 3450 },
    { month: "Dec-25", value: 3920 },
    { month: "Jan-26", value: 2780 },
  ];

  const transactionsData = [
    { month: "Jun-25", value: 420 },
    { month: "Jul-25", value: 510 },
    { month: "Aug-25", value: 380 },
    { month: "Sep-25", value: 290 },
    { month: "Oct-25", value: 360 },
    { month: "Nov-25", value: 470 },
    { month: "Dec-25", value: 540 },
    { month: "Jan-26", value: 410 },
  ];

  const outletwiseData = [
    { outlet: "Outlet1", transactions: 840, members: 320 },
    { outlet: "Outlet2", transactions: 920, members: 280 },
    { outlet: "Outlet3", transactions: 1150, members: 410 },
    { outlet: "Outlet4", transactions: 780, members: 250 },
    { outlet: "Outlet5", transactions: 1340, members: 490 },
  ];

  const onlyOnceData = [
    { category: "Passive, High Spend", members: 420 },
    { category: "Passive, Low Spend", members: 280 },
    { category: "Active, High Spend", members: 510 },
    { category: "Active, Low Spend", members: 190 },
  ];

  const platformContributionData = [
    { name: "Redemption", value: 55 },
    { name: "Campaign", value: 25 },
    { name: "Celebration", value: 20 },
  ];

  const PIE_COLORS = ["#5eead4", "#fb923c", "#4ade80"];

  const todayStats = [
    { label: "No of Transaction", value: "48" },
    { label: "Revenue", value: "3,240" },
    { label: "Points Earned", value: "860" },
    { label: "Points Redeemed", value: "215" },
    { label: "New Enrollment", value: "12" },
  ];

  const stats = [
    {
      title: "Total Revenue",
      value: "8,450",
      icon: Award,
      color: "text-green-500",
      bgColor: "bg-green-100",
    },
    {
      title: "Redemption Revenue",
      value: "2,180",
      icon: BarChartIcon,
      color: "text-blue-500",
      bgColor: "bg-blue-100",
    },
    {
      title: "Campaign Revenue",
      value: "1,650",
      icon: CreditCard,
      color: "text-purple-500",
      bgColor: "bg-purple-100",
    },
    {
      title: "Total Members",
      value: "3,240",
      icon: Users,
      color: "text-amber-500",
      bgColor: "bg-amber-100",
    },
    {
      title: "Active Members",
      value: "2,180",
      icon: Users,
      color: "text-indigo-500",
      bgColor: "bg-indigo-100",
    },
    {
      title: "Total Transactions",
      value: "5,620",
      icon: CreditCard,
      color: "text-pink-500",
      bgColor: "bg-pink-100",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome to Perk Points Dashboard.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <div className={`${stat.bgColor} p-2 rounded-md`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Section */}
      <div className="space-y-6">
        {/* Business Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Business</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={businessData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 5000]} tickFormatter={formatYAxisTick} />
                  <Tooltip
                    formatter={(value: number) => [
                      value.toLocaleString(),
                      "Business",
                    ]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#fb923c"
                    fill="#fed7aa"
                    fillOpacity={0.8}
                    name="Business"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={transactionsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 600]} />
                  <Tooltip
                    formatter={(value: number) => [
                      value.toLocaleString(),
                      "Transactions",
                    ]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#60a5fa"
                    fill="#bfdbfe"
                    fillOpacity={0.8}
                    name="Transactions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bar Charts Section */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Outletwise Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Outletwise</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={outletwiseData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 1600]} />
                    <YAxis dataKey="outlet" type="category" width={80} />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        value.toLocaleString(),
                        name,
                      ]}
                    />
                    <Legend />
                    <Bar
                      dataKey="transactions"
                      fill="#fb923c"
                      name="Transactions"
                    />
                    <Bar dataKey="members" fill="#60a5fa" name="Members" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Only Once Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Only Once</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={onlyOnceData}
                    margin={{ top: 10, right: 30, left: 20, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="category"
                      interval={0}
                      height={50}
                      tick={(props: any) => {
                        const { x, y, payload } = props;
                        const parts = payload.value.split(", ");
                        return (
                          <g transform={`translate(${x},${y + 5})`}>
                            <text
                              x={0}
                              y={0}
                              textAnchor="middle"
                              fill="#666"
                              fontSize={12}
                            >
                              {parts.map((part: string, i: number) => (
                                <tspan key={i} x={0} dy={i === 0 ? 0 : 14}>
                                  {part}
                                </tspan>
                              ))}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis
                      type="number"
                      domain={[0, "dataMax + 50"]}
                      tickCount={7}
                      allowDecimals={false}
                      width={60}
                      tickFormatter={(value: number) =>
                        value === 0 ? "" : value.toString()
                      }
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        value.toLocaleString(),
                        name,
                      ]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="members"
                      name="Members"
                      stroke="#60a5fa"
                      strokeWidth={3}
                      dot={{ r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pie Chart and Statistics Section */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Platform Contribution Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Platform Contribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={platformContributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {platformContributionData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Today's Statistics Table */}
          <Card>
            <CardHeader>
              <CardTitle>Today's Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {todayStats.map((stat, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b last:border-b-0"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {stat.label}:
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
